import { useFormContext, Controller } from "react-hook-form"
import useOnClickOutside from "@/lib/hooks/useOnClickOutside"
import { Caret } from "@/common/icons/caret"
import Close from "@/common/icons/close"
import { useState, useRef, useEffect } from "react"
import classNames from "classnames"
import { ErrorMessage } from "@hookform/error-message"
import styles from "./activity-control.module.css"

export const ActivityControl = ({
  options,
  control,
  name,
  multiple,
  data,
  placeholder
}) => {
  const {
    formState: { errors },
    resetField,
    watch
  } = useFormContext()
  const [open, setOpen] = useState(false)
  const defaultLabel = placeholder
  const [currentLabel, setCurrentLabel] = useState(defaultLabel)
  const [currentIcon, setCurrentIcon] = useState(false)
  const fieldValue = watch(name)
  const listRef = useRef(null)

  useOnClickOutside(listRef, () => setOpen(false), open)

  const getHasValue = (multiple, value) => {
    return multiple ? value.length > 0 : typeof value === "number"
  }

  useEffect(() => {
    if (!getHasValue(multiple, fieldValue)) {
      setCurrentLabel(defaultLabel)
      setCurrentIcon(false)
      return
    }

    if (data) {
      setCurrentLabel(data.title)
      setCurrentIcon(data.icon)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue])

  return (
    <Controller
      {...{ control, name }}
      rules={{ required: !multiple ? "Detta fält är obligatoriskt" : false }}
      render={({ field }) => {
        const hasValue = getHasValue(multiple, field.value)
        return (
          <>
            <div
              className={classNames(styles.controller, {
                "is-open": open,
                "has-value": hasValue
              })}
              ref={listRef}>
              <button
                type="button"
                onClick={() => setOpen(!open)}
                aria-label={open ? "Stäng" : "Öppna"}>
                {!multiple && hasValue && (
                  <Icon className="current-icon" svg={currentIcon} />
                )}
                <span>{currentLabel}</span>
                {hasValue && (
                  <div
                    className="clear-filter"
                    onClick={(e) => {
                      e.stopPropagation()
                      resetField(name)
                    }}>
                    <Close />
                  </div>
                )}
                <Caret className="caret" />
              </button>
              <Activities
                {...{
                  field,
                  multiple,
                  options,
                  defaultLabel,
                  setCurrentLabel,
                  setCurrentIcon,
                  setOpen
                }}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name={name}
              render={({ message }) => <p className="error">{message}</p>}
            />
          </>
        )
      }}
    />
  )
}

const Activities = ({
  field,
  multiple,
  options,
  defaultLabel,
  setCurrentLabel,
  setCurrentIcon,
  setOpen
}) => {
  return (
    <ul className="activites">
      {options.map(({ category, activities }, index) => {
        if (activities.length === 0) {
          return null
        }
        return (
          <li key={index}>
            <h5>{category}</h5>
            <ul>
              {activities.map(({ id, title, icon }) => {
                const isChecked = multiple
                  ? field.value.includes(id)
                  : field.value === id
                return (
                  <label
                    key={id}
                    className={classNames("activity", {
                      "is-checked": isChecked
                    })}>
                    <input
                      type={multiple ? "checkbox" : "radio"} // or "radio" for radio buttons
                      value={id}
                      checked={isChecked}
                      onChange={() => {
                        let newValue
                        if (multiple) {
                          newValue = field.value.includes(id)
                            ? field.value.filter((v) => v !== id)
                            : [...field.value, id]
                          const text =
                            newValue.length === 1
                              ? " aktivitet vald"
                              : " aktiviteter valda"
                          const label = newValue.length + " " + text
                          setCurrentLabel(
                            newValue.length > 0 ? label : defaultLabel
                          )
                        } else {
                          newValue = id
                          setCurrentIcon(icon)
                          setCurrentLabel(title)
                          setOpen(false)
                        }
                        field.onChange(newValue)
                      }}
                    />
                    <Icon svg={icon} />
                    <span>{title}</span>
                  </label>
                )
              })}
            </ul>
          </li>
        )
      })}
    </ul>
  )
}

const Icon = ({ svg, className }) => {
  return (
    <div
      className={classNames("icon", className)}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}
